import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Switch, 
  FormControlLabel, 
  TextField, 
  Button, 
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import debugService from '../services/debugService';  // 🐛 NEW: Debug Service

// API functions
const botSettingsApi = {
  getStatus: () => axios.get('/api/bot-settings/status').then(res => res.data),
  updateSettings: (settings) => axios.post('/api/bot-settings/update', settings).then(res => res.data),
  controlBot: (action) => axios.post('/api/bot-settings/control', { action }).then(res => res.data),
  clearCache: () => Promise.all([
    axios.post('/api/bot/cache/clear').then(res => res.data),
    axios.post('/api/bot/cache/argus-tokens/clear').then(res => res.data)
  ])
};

// 🔐 Create authenticated axios instance for credentials
const authenticatedAxios = (() => {
  const api = axios.create({
    baseURL: '/api',
    headers: { 'Content-Type': 'application/json' }
  });
  
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  
  return api;
})();

// 🔐 Credentials API functions
const credentialsApi = {
  getAll: () => authenticatedAxios.get('/credentials').then(res => res.data),
  getByType: (type) => authenticatedAxios.get(`/credentials/type/${type}`).then(res => res.data),
  create: (credential) => authenticatedAxios.post('/credentials', credential).then(res => res.data),
  update: (id, credential) => authenticatedAxios.put(`/credentials/${id}`, credential).then(res => res.data),
  delete: (id) => authenticatedAxios.delete(`/credentials/${id}`).then(res => res.data),
  batchUpdate: (credentials) => authenticatedAxios.post('/credentials/batch-update', { credentials }).then(res => res.data)
};

// 👥 Users API functions (reuse authenticated axios)
const usersApi = {
  getAll: () => authenticatedAxios.get('/users').then(res => res.data)
};

// 📱 SMS API functions
const smsApi = {
  getSettings: () => authenticatedAxios.get('/sms/settings/full').then(res => res.data),
  saveSettings: (settings) => authenticatedAxios.post('/sms/settings', settings).then(res => res.data),
  getTemplates: () => authenticatedAxios.get('/sms/templates').then(res => res.data),
  createTemplate: (template) => authenticatedAxios.post('/sms/templates', template).then(res => res.data),
  updateTemplate: (id, template) => authenticatedAxios.put(`/sms/templates/${id}`, template).then(res => res.data),
  deleteTemplate: (id) => authenticatedAxios.delete(`/sms/templates/${id}`).then(res => res.data)
};

// 🔐 Credentials Management Component
const CredentialsManagement = () => {
  const queryClient = useQueryClient();
  const [credentials, setCredentials] = useState([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [newCredential, setNewCredential] = useState({
    credential_type: 'zoom',
    credential_name: '',
    credential_value: '',
    description: '',
    encrypt_value: false,
    user_id: null  // 🆕 NEW: User ID for per-user credentials
  });
  const [showValues, setShowValues] = useState({});
  
  // 🆕 NEW: Quick Zoom API Setup Dialog
  const [zoomQuickSetupOpen, setZoomQuickSetupOpen] = useState(false);
  const [zoomQuickSetupUser, setZoomQuickSetupUser] = useState(null);
  const [zoomQuickSetupData, setZoomQuickSetupData] = useState({
    client_id: '',
    client_secret: '',
    account_id: '',
    api_label: ''
  });

  // Fetch credentials
  const { data: credentialsData, isLoading: credentialsLoading, error: credentialsError, refetch } = useQuery(
    'credentials',
    credentialsApi.getAll,
    {
      onSuccess: (data) => {
        if (data.success && data.data) {
          setCredentials(data.data);
        }
      },
      refetchInterval: false // Don't auto-refresh sensitive data
    }
  );

  // 🆕 NEW: Fetch users for per-user credentials
  const { data: usersData, isLoading: usersLoading, error: usersError } = useQuery(
    'users',
    usersApi.getAll,
    {
      refetchInterval: false,
      onSuccess: (data) => {
        console.log('✅ Users loaded:', data);
      },
      onError: (error) => {
        console.error('❌ Failed to load users:', error);
        toast.error('Failed to load users. Please check console.');
      }
    }
  );

  const users = usersData?.data || [];
  console.log('👥 Users array:', users, 'Length:', users.length);

  // 🆕 Group credentials by user
  const credentialsByUser = {};
  credentials.forEach(cred => {
    const userId = cred.user_id || 'global';
    if (!credentialsByUser[userId]) {
      credentialsByUser[userId] = [];
    }
    credentialsByUser[userId].push(cred);
  });

  // Update credential mutation
  const updateCredentialMutation = useMutation(
    ({ id, ...credential }) => credentialsApi.update(id, credential),
    {
      onSuccess: () => {
        toast.success('✅ Credential updated successfully!');
        queryClient.invalidateQueries('credentials');
        setEditDialogOpen(false);
        setEditingCredential(null);
      },
      onError: (error) => {
        toast.error(`❌ Error: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  // Create credential mutation
  const createCredentialMutation = useMutation(
    credentialsApi.create,
    {
      onSuccess: () => {
        toast.success('✅ Credential created successfully!');
        queryClient.invalidateQueries('credentials');
        setEditDialogOpen(false);
        setNewCredential({
          credential_type: 'general',
          credential_name: '',
          credential_value: '',
          description: '',
          encrypt_value: false
        });
      },
      onError: (error) => {
        toast.error(`❌ Error: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  // Delete credential mutation
  const deleteCredentialMutation = useMutation(
    credentialsApi.delete,
    {
      onSuccess: () => {
        toast.success('✅ Credential deleted successfully!');
        queryClient.invalidateQueries('credentials');
      },
      onError: (error) => {
        toast.error(`❌ Error: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  const handleEdit = (credential) => {
    // Fetch full credential data including hidden values
    credentialsApi.getByType(credential.credential_type).then((response) => {
      if (response.success) {
        const fullCredential = response.data.find(c => c.id === credential.id);
        if (fullCredential) {
          setEditingCredential({
            ...fullCredential,
            encrypt_value: fullCredential.is_encrypted
          });
          setEditDialogOpen(true);
        }
      }
    }).catch((error) => {
      toast.error(`Error fetching credential: ${error.message}`);
    });
  };

  const handleSave = () => {
    if (editingCredential?.id) {
      updateCredentialMutation.mutate(editingCredential);
    } else {
      createCredentialMutation.mutate(newCredential);
    }
  };

  const handleDelete = (credential) => {
    if (window.confirm(`Είστε σίγουροι ότι θέλετε να διαγράψετε το credential "${credential.credential_name}"?`)) {
      deleteCredentialMutation.mutate(credential.id);
    }
  };
  
  // 🆕 NEW: Quick Zoom API Setup Handler
  const handleZoomQuickSetup = async () => {
    let loadingToast;
    try {
      const { client_id, client_secret, account_id, api_label } = zoomQuickSetupData;
      
      if (!client_id || !client_secret || !account_id) {
        toast.error('Please fill in all required fields!');
        return;
      }
      
      if (!zoomQuickSetupUser) {
        toast.error('No user selected!');
        return;
      }
      
      loadingToast = toast.loading('Saving Zoom API credentials...');
      
      // Get existing credentials for this user
      const userCreds = credentialsByUser[zoomQuickSetupUser.id] || [];
      const existingClientId = userCreds.find(c => c.credential_name === 'zoom_client_id');
      const existingClientSecret = userCreds.find(c => c.credential_name === 'zoom_client_secret');
      const existingAccountId = userCreds.find(c => c.credential_name === 'zoom_account_id');
      const existingApiLabel = userCreds.find(c => c.credential_name === 'zoom_api_label');
      
      // Prepare credentials data
      const credentialOperations = [];
      
      // Client ID
      if (existingClientId) {
        credentialOperations.push(
          credentialsApi.update(existingClientId.id, {
            credential_value: client_id,
            description: api_label ? `Zoom Client ID (${api_label})` : 'Zoom Client ID',
            user_id: zoomQuickSetupUser.id
          })
        );
      } else {
        credentialOperations.push(
          credentialsApi.create({
            credential_type: 'zoom',
            credential_name: 'zoom_client_id',
            credential_value: client_id,
            description: api_label ? `Zoom Client ID (${api_label})` : 'Zoom Client ID',
            encrypt_value: false,
            user_id: zoomQuickSetupUser.id
          })
        );
      }
      
      // Client Secret
      if (existingClientSecret) {
        credentialOperations.push(
          credentialsApi.update(existingClientSecret.id, {
            credential_value: client_secret,
            description: api_label ? `Zoom Client Secret (${api_label})` : 'Zoom Client Secret',
            user_id: zoomQuickSetupUser.id
          })
        );
      } else {
        credentialOperations.push(
          credentialsApi.create({
            credential_type: 'zoom',
            credential_name: 'zoom_client_secret',
            credential_value: client_secret,
            description: api_label ? `Zoom Client Secret (${api_label})` : 'Zoom Client Secret',
            encrypt_value: true,
            user_id: zoomQuickSetupUser.id
          })
        );
      }
      
      // Account ID
      if (existingAccountId) {
        credentialOperations.push(
          credentialsApi.update(existingAccountId.id, {
            credential_value: account_id,
            description: api_label ? `Zoom Account ID (${api_label})` : 'Zoom Account ID',
            user_id: zoomQuickSetupUser.id
          })
        );
      } else {
        credentialOperations.push(
          credentialsApi.create({
            credential_type: 'zoom',
            credential_name: 'zoom_account_id',
            credential_value: account_id,
            description: api_label ? `Zoom Account ID (${api_label})` : 'Zoom Account ID',
            encrypt_value: false,
            user_id: zoomQuickSetupUser.id
          })
        );
      }
      
      // API Label (if provided)
      if (api_label) {
        if (existingApiLabel) {
          credentialOperations.push(
            credentialsApi.update(existingApiLabel.id, {
              credential_value: api_label,
              description: 'Zoom API Label for identification',
              user_id: zoomQuickSetupUser.id
            })
          );
        } else {
          credentialOperations.push(
            credentialsApi.create({
              credential_type: 'zoom',
              credential_name: 'zoom_api_label',
              credential_value: api_label,
              description: 'Zoom API Label for identification',
              encrypt_value: false,
              user_id: zoomQuickSetupUser.id
            })
          );
        }
      }
      
      await Promise.all(credentialOperations);
      
      toast.dismiss(loadingToast);
      toast.success(`✅ Zoom API credentials saved for ${zoomQuickSetupUser.username}!`);
      
      // Reset and close
      setZoomQuickSetupData({ client_id: '', client_secret: '', account_id: '', api_label: '' });
      setZoomQuickSetupUser(null);
      setZoomQuickSetupOpen(false);
      
      // Refresh credentials list
      refetch();
      
    } catch (error) {
      console.error('Failed to create Zoom credentials:', error);
      if (loadingToast) {
        toast.dismiss(loadingToast);
      }
      toast.error(`Failed to create credentials: ${error.response?.data?.error || error.message}`);
    }
  };

  const toggleShowValue = (credentialId) => {
    setShowValues(prev => ({
      ...prev,
      [credentialId]: !prev[credentialId]
    }));
  };

  const getCredentialTypeColor = (type) => {
    switch (type) {
      case 'zoom': return 'primary';
      case 'bot': return 'secondary'; 
      case 'moodle': return 'success';
      case 'database': return 'error';
      case 'general': return 'default';
      default: return 'default';
    }
  };

  const credentialToEdit = editingCredential || newCredential;
  const isEditing = !!editingCredential?.id;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKeyIcon />
          API Credentials Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingCredential(null);
            setEditDialogOpen(true);
          }}
        >
          Add New Credential
        </Button>
      </Box>

      {credentialsLoading || usersLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : credentialsError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading credentials: {credentialsError.message}
        </Alert>
      ) : (
        <Box>
          {/* 🌍 Global Credentials */}
          {credentialsByUser['global'] && credentialsByUser['global'].length > 0 && (
            <Card sx={{ mb: 3, border: '2px solid #e0e0e0' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  🌍 Global Credentials
                  <Chip label="All Users" size="small" color="default" />
                </Typography>
                <Grid container spacing={2}>
                  {credentialsByUser['global'].map((credential) => (
                    <Grid item xs={12} key={credential.id}>
                      <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                            {credential.credential_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {credential.description || 'No description'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#666' }}>
                            {showValues[credential.id] 
                              ? credential.credential_value_preview || credential.credential_value
                              : credential.credential_value_preview
                            }
                          </Typography>
                          {credential.credential_name.includes('secret') || credential.credential_name.includes('token') ? (
                            <IconButton size="small" onClick={() => toggleShowValue(credential.id)}>
                              {showValues[credential.id] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          ) : null}
                          <IconButton size="small" onClick={() => handleEdit(credential)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(credential)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* 👥 Per-User Credentials */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            👥 User Zoom API Credentials
          </Typography>
          
          {usersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, width: '100%' }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading users...</Typography>
            </Box>
          ) : usersError ? (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              Failed to load users: {usersError.message}
            </Alert>
          ) : users.length === 0 ? (
            <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
              No users found in the system. Please create users first.
            </Alert>
          ) : (
          <Grid container spacing={2}>
            {users.map((user) => {
              const userCreds = credentialsByUser[user.id] || [];
              const zoomCreds = userCreds.filter(c => c.credential_type === 'zoom');
              
              return (
                <Grid item xs={12} md={6} key={user.id}>
                  <Card sx={{ 
                    border: zoomCreds.length > 0 ? '2px solid #2088ff' : '2px dashed #ccc',
                    height: '100%'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: '#2088ff' }}>
                          {user.username}
                        </Typography>
                        <Chip 
                          label={user.role} 
                          size="small" 
                          color={user.role === 'admin' ? 'error' : 'primary'}
                        />
                      </Box>

                      {zoomCreds.length === 0 ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          No Zoom credentials configured for this user
                        </Alert>
                      ) : (
                        <Box sx={{ mb: 2 }}>
                          {zoomCreds.map((credential) => (
                            <Box key={credential.id} sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>
                                  {credential.credential_name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {(credential.credential_name.includes('secret') || credential.credential_name.includes('token')) && (
                                    <IconButton size="small" onClick={() => toggleShowValue(credential.id)}>
                                      {showValues[credential.id] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </IconButton>
                                  )}
                                  <IconButton size="small" onClick={() => handleEdit(credential)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => handleDelete(credential)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                              <TextField
                                fullWidth
                                value={showValues[credential.id] 
                                  ? credential.credential_value_preview || credential.credential_value
                                  : credential.credential_value_preview || '***'}
                                size="small"
                                InputProps={{
                                  readOnly: true,
                                  sx: { fontFamily: 'monospace', fontSize: '0.85rem', bgcolor: '#f5f5f5' }
                                }}
                              />
                            </Box>
                          ))}
                        </Box>
                      )}

                      <Button
                        fullWidth
                        variant={zoomCreds.length > 0 ? "contained" : "outlined"}
                        startIcon={zoomCreds.length > 0 ? <EditIcon /> : <AddIcon />}
                        onClick={() => {
                          setZoomQuickSetupUser(user);
                          // Pre-fill with existing values if available
                          if (zoomCreds.length > 0) {
                            const clientId = zoomCreds.find(c => c.credential_name === 'zoom_client_id');
                            const clientSecret = zoomCreds.find(c => c.credential_name === 'zoom_client_secret');
                            const accountId = zoomCreds.find(c => c.credential_name === 'zoom_account_id');
                            const apiLabel = zoomCreds.find(c => c.credential_name === 'zoom_api_label');
                            setZoomQuickSetupData({
                              client_id: clientId?.credential_value || '',
                              client_secret: '',  // Don't pre-fill secrets
                              account_id: accountId?.credential_value || '',
                              api_label: apiLabel?.credential_value || ''
                            });
                          } else {
                            setZoomQuickSetupData({ client_id: '', client_secret: '', account_id: '', api_label: '' });
                          }
                          setZoomQuickSetupOpen(true);
                        }}
                      >
                        {zoomCreds.length > 0 ? `Edit Zoom API for ${user.username}` : `Add Zoom API for ${user.username}`}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          )}
        </Box>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? 'Edit Credential' : 'Add New Credential'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Credential Type</InputLabel>
                <Select
                  value={credentialToEdit.credential_type}
                  onChange={(e) => {
                    const target = isEditing ? setEditingCredential : setNewCredential;
                    target(prev => ({ ...prev, credential_type: e.target.value }));
                  }}
                  disabled={isEditing}
                >
                  <MenuItem value="zoom">Zoom API</MenuItem>
                  <MenuItem value="bot">Bot System</MenuItem>
                  <MenuItem value="moodle">Moodle API</MenuItem>
                  <MenuItem value="database">Database</MenuItem>
                  <MenuItem value="general">General</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>User (for per-user credentials)</InputLabel>
                <Select
                  value={credentialToEdit.user_id || ''}
                  onChange={(e) => {
                    const target = isEditing ? setEditingCredential : setNewCredential;
                    target(prev => ({ ...prev, user_id: e.target.value || null }));
                  }}
                  disabled={usersLoading}
                >
                  <MenuItem value="">
                    <em>Global (All Users)</em>
                  </MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.username} ({user.role})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Credential Name"
                value={credentialToEdit.credential_name}
                onChange={(e) => {
                  const target = isEditing ? setEditingCredential : setNewCredential;
                  target(prev => ({ ...prev, credential_name: e.target.value }));
                }}
                disabled={isEditing}
                placeholder="e.g., zoom_client_id"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Credential Value"
                value={credentialToEdit.credential_value}
                onChange={(e) => {
                  const target = isEditing ? setEditingCredential : setNewCredential;
                  target(prev => ({ ...prev, credential_value: e.target.value }));
                }}
                type={credentialToEdit.credential_name?.includes('secret') || credentialToEdit.credential_name?.includes('token') ? 'password' : 'text'}
                placeholder="Enter the credential value..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={credentialToEdit.description}
                onChange={(e) => {
                  const target = isEditing ? setEditingCredential : setNewCredential;
                  target(prev => ({ ...prev, description: e.target.value }));
                }}
                multiline
                rows={2}
                placeholder="Brief description of this credential..."
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={credentialToEdit.encrypt_value || false}
                    onChange={(e) => {
                      const target = isEditing ? setEditingCredential : setNewCredential;
                      target(prev => ({ ...prev, encrypt_value: e.target.checked }));
                    }}
                  />
                }
                label="Encrypt this credential in database"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                💡 Sensitive credentials (secrets, tokens, passwords) are automatically encrypted
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={updateCredentialMutation.isLoading || createCredentialMutation.isLoading}
          >
            {(updateCredentialMutation.isLoading || createCredentialMutation.isLoading) ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Saving...
              </>
            ) : (
              isEditing ? 'Update' : 'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🆕 NEW: Quick Zoom API Setup Dialog */}
      <Dialog 
        open={zoomQuickSetupOpen} 
        onClose={() => setZoomQuickSetupOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          🔑 Zoom API Configuration for {zoomQuickSetupUser?.username}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3, mt: 2 }}>
            Fill in all 3 required Zoom API credentials. Existing credentials will be updated automatically.
          </Alert>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Label (Optional)"
                value={zoomQuickSetupData.api_label}
                onChange={(e) => setZoomQuickSetupData(prev => ({ ...prev, api_label: e.target.value }))}
                placeholder="e.g., Personal API, Work API, etc."
                helperText="A friendly name to identify this API in the system"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Client ID"
                value={zoomQuickSetupData.client_id}
                onChange={(e) => setZoomQuickSetupData(prev => ({ ...prev, client_id: e.target.value }))}
                placeholder="Enter Zoom Client ID"
                InputProps={{
                  sx: { fontFamily: 'monospace' }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Client Secret"
                type="password"
                value={zoomQuickSetupData.client_secret}
                onChange={(e) => setZoomQuickSetupData(prev => ({ ...prev, client_secret: e.target.value }))}
                placeholder="Enter Zoom Client Secret"
                InputProps={{
                  sx: { fontFamily: 'monospace' }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Account ID"
                value={zoomQuickSetupData.account_id}
                onChange={(e) => setZoomQuickSetupData(prev => ({ ...prev, account_id: e.target.value }))}
                placeholder="Enter Zoom Account ID"
                InputProps={{
                  sx: { fontFamily: 'monospace' }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZoomQuickSetupOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleZoomQuickSetup}
            disabled={!zoomQuickSetupData.client_id || !zoomQuickSetupData.client_secret || !zoomQuickSetupData.account_id}
          >
            Save Credentials
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// 📱 SMS Management Component
const SMSManagement = () => {
  const queryClient = useQueryClient();
  const [smsSettings, setSmsSettings] = useState({
    api_key: '',
    sender_name: 'Merimna',
    enabled: true
  });
  const [templates, setTemplates] = useState([]);
  const [editTemplateDialog, setEditTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);

  // Fetch SMS settings
  const { data: settingsData, isLoading: settingsLoading } = useQuery(
    'sms-settings',
    smsApi.getSettings,
    {
      onSuccess: (data) => {
        if (data.success && data.settings) {
          setSmsSettings(data.settings);
        }
      },
      refetchInterval: false
    }
  );

  // Fetch SMS templates
  const { data: templatesData, isLoading: templatesLoading } = useQuery(
    'sms-templates',
    smsApi.getTemplates,
    {
      onSuccess: (data) => {
        if (data.success && data.templates) {
          setTemplates(data.templates);
        }
      },
      refetchInterval: false
    }
  );

  // Save settings mutation
  const saveSettingsMutation = useMutation(smsApi.saveSettings, {
    onSuccess: () => {
      toast.success('SMS settings saved successfully');
      queryClient.invalidateQueries('sms-settings');
    },
    onError: (error) => {
      toast.error(`Error saving SMS settings: ${error.response?.data?.error || error.message}`);
    }
  });

  // Update template mutation
  const updateTemplateMutation = useMutation(
    ({ id, template }) => smsApi.updateTemplate(id, template),
    {
      onSuccess: () => {
        toast.success('Template updated successfully');
        queryClient.invalidateQueries('sms-templates');
        setEditTemplateDialog(false);
        setEditingTemplate(null);
      },
      onError: (error) => {
        toast.error(`Error updating template: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  const handleSaveSettings = () => {
    if (!smsSettings.api_key || !smsSettings.sender_name) {
      toast.error('API Key and Sender Name are required');
      return;
    }
    saveSettingsMutation.mutate(smsSettings);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({ ...template });
    setEditTemplateDialog(true);
  };

  const handleSaveTemplate = () => {
    if (!editingTemplate.template_name || !editingTemplate.template_content) {
      toast.error('Template name and content are required');
      return;
    }
    updateTemplateMutation.mutate({
      id: editingTemplate.id,
      template: editingTemplate
    });
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        📱 SMS Configuration (Brevo)
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Configure Brevo SMS settings to send SMS messages to participants during live monitoring.
        Get your API key from <a href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noopener noreferrer">Brevo Settings</a>
      </Alert>

      {/* SMS Settings */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          API Settings
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Brevo API Key"
              fullWidth
              type={showApiKey ? 'text' : 'password'}
              value={smsSettings.api_key}
              onChange={(e) => setSmsSettings({ ...smsSettings, api_key: e.target.value })}
              placeholder="xkeysib-..."
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowApiKey(!showApiKey)} edge="end">
                    {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Sender Name"
              fullWidth
              value={smsSettings.sender_name}
              onChange={(e) => setSmsSettings({ ...smsSettings, sender_name: e.target.value })}
              placeholder="Merimna"
              helperText="Max 11 alphanumeric characters"
              inputProps={{ maxLength: 11 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={smsSettings.enabled}
                  onChange={(e) => setSmsSettings({ ...smsSettings, enabled: e.target.checked })}
                />
              }
              label="Enable SMS Service"
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={saveSettingsMutation.isLoading}
            >
              {saveSettingsMutation.isLoading ? 'Saving...' : 'Save Settings'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* SMS Templates */}
      <Box>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          SMS Templates
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Edit your SMS message templates. Use {'{name}'} as placeholder for student name.
        </Typography>

        {templatesLoading ? (
          <CircularProgress size={24} />
        ) : (
          <Grid container spacing={2}>
            {templates.map((template) => (
              <Grid item xs={12} key={template.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {template.template_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {template.template_content}
                        </Typography>
                        {template.description && (
                          <Typography variant="caption" color="text.secondary">
                            {template.description}
                          </Typography>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleEditTemplate(template)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Edit Template Dialog */}
      <Dialog open={editTemplateDialog} onClose={() => setEditTemplateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit SMS Template</DialogTitle>
        <DialogContent>
          {editingTemplate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Template Name"
                fullWidth
                value={editingTemplate.template_name}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, template_name: e.target.value })}
              />
              <TextField
                label="Message Content"
                fullWidth
                multiline
                rows={4}
                value={editingTemplate.template_content}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, template_content: e.target.value })}
                helperText="Use {name} for student name placeholder"
              />
              <TextField
                label="Description"
                fullWidth
                value={editingTemplate.description || ''}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTemplateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSaveTemplate}
            variant="contained"
            disabled={updateTemplateMutation.isLoading}
          >
            {updateTemplateMutation.isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const BotSystemStatus = ({ status }) => {
  const getStatusColor = (operationalStatus) => {
    switch (operationalStatus) {
      case 'active': return 'success';
      case 'enabled_but_offline': return 'warning'; 
      case 'scheduled_but_offline': return 'warning';
      case 'scheduled_but_outside_hours': return 'info';
      case 'disabled': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (operationalStatus) => {
    switch (operationalStatus) {
      case 'active': return 'Ενεργό & Υγιές';
      case 'enabled_but_offline': return 'Ενεργοποιημένο αλλά Offline';
      case 'scheduled_but_offline': return 'Προγραμματισμένο αλλά Offline'; 
      case 'scheduled_but_outside_hours': return 'Εκτός Ωραρίου';
      case 'disabled': return 'Απενεργοποιημένο';
      default: return 'Άγνωστη Κατάσταση';
    }
  };

  // 🔧 SAFE ACCESS: Handle undefined/null status gracefully
  if (!status || !status.runtime_status) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            🤖 Bot System Status
          </Typography>
          <Alert severity="warning">
            ⚠️ Δεν είναι δυνατή η φόρτωση των στοιχείων Bot System. 
            Παρακαλώ ελέγξτε αν η βάση δεδομένων και το API λειτουργούν σωστά.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const runtimeStatus = status.runtime_status || {};
  const operationalStatus = runtimeStatus.operational_status || 'unknown';
  const isRunning = runtimeStatus.is_running || false;
  const isHealthy = runtimeStatus.is_healthy || false;
  const processInfo = runtimeStatus.process_info || null;

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          🤖 Bot System Status
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" sx={{ minWidth: 120 }}>
                Κατάσταση:
              </Typography>
              <Chip 
                label={getStatusText(operationalStatus)}
                color={getStatusColor(operationalStatus)}
                size="small"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" sx={{ minWidth: 120 }}>
                Process:
              </Typography>
              <Chip 
                label={isRunning ? 'Τρέχει' : 'Σταματημένο'}
                color={isRunning ? 'success' : 'error'}
                size="small"
              />
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" sx={{ minWidth: 120 }}>
                Υγεία:
              </Typography>
              <Chip 
                label={isHealthy ? 'Υγιές' : 'Πρόβλημα'}
                color={isHealthy ? 'success' : 'error'}
                size="small"
              />
            </Box>
          </Grid>
          
          {processInfo && (
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                PID: {processInfo.pid || 'N/A'} | 
                Memory: {processInfo.memory || '0'}MB | 
                CPU: {processInfo.cpu || '0'}% | 
                Restarts: {processInfo.restarts || '0'}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

const Settings = () => {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState({
    manual_enabled: true,
    schedule_enabled: false,
    start_time: '04:00',
    end_time: '22:00',
    timezone: 'Europe/Athens',
    debug_enabled: false  // 🐛 NEW: Debug ON/OFF setting
  });

  // Fetch Bot System status
  const { data: statusData, isLoading: statusLoading, error: statusError } = useQuery(
    'bot-status', 
    botSettingsApi.getStatus,
    {
      refetchInterval: 10000, // Refresh every 10 seconds
      onSuccess: (data) => {
        if (data.settings) {
          setSettings({
            manual_enabled: data.settings.manual_enabled,
            schedule_enabled: data.settings.schedule_enabled,
            start_time: data.settings.start_time?.slice(0, 5) || '04:00', // Convert from HH:MM:SS to HH:MM
            end_time: data.settings.end_time?.slice(0, 5) || '22:00',
            timezone: data.settings.timezone || 'Europe/Athens',
            debug_enabled: data.settings.debug_enabled || false  // 🐛 NEW: Load debug setting
          });
        }
      }
    }
  );

  // Update settings mutation
  const updateSettingsMutation = useMutation(
    botSettingsApi.updateSettings,
    {
      onSuccess: (data) => {
        toast.success('✅ Ρυθμίσεις ενημερώθηκαν επιτυχώς!');
        queryClient.invalidateQueries('bot-status');
      },
      onError: (error) => {
        toast.error(`❌ Σφάλμα: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  // Bot control mutations
  const controlMutation = useMutation(
    botSettingsApi.controlBot,
    {
      onSuccess: (data) => {
        toast.success(`✅ Bot System ${data.action} επιτυχώς!`);
        queryClient.invalidateQueries('bot-status');
      },
      onError: (error) => {
        toast.error(`❌ Σφάλμα: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  // Clear cache mutation
  const clearCacheMutation = useMutation(
    botSettingsApi.clearCache,
    {
      onSuccess: (data) => {
        toast.success(`🧹 Cache καθαρίστηκε επιτυχώς!`);
        debugService.log('🧹 Bot cache cleared:', data);
      },
      onError: (error) => {
        toast.error(`❌ Σφάλμα καθαρισμού cache: ${error.response?.data?.error || error.message}`);
      }
    }
  );

  const handleSettingsChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = () => {
    updateSettingsMutation.mutate(settings);
  };

  const handleBotControl = (action) => {
    controlMutation.mutate(action);
  };

  const handleClearCache = () => {
    clearCacheMutation.mutate();
  };

  // 🐛 NEW: Update debug service when debug setting changes
  useEffect(() => {
    debugService.updateSetting(settings.debug_enabled);
  }, [settings.debug_enabled]);

  if (statusLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem', color: 'rgba(0, 0, 0, 0.87)' }}>
        System Settings
      </Typography>

      {statusError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Σφάλμα φόρτωσης ρυθμίσεων: {statusError.message}
        </Alert>
      )}

      {/* Credentials Management */}
      <CredentialsManagement />

      {/* SMS Management */}
      <SMSManagement />

      {/* Bot System Status */}
      {statusData && <BotSystemStatus status={statusData} />}

      {/* Bot System Controls */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          🎮 Bot System Control
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayArrowIcon />}
            onClick={() => handleBotControl('start')}
            disabled={controlMutation.isLoading}
          >
            Εκκίνηση
          </Button>
          
          <Button
            variant="contained"
            color="error"  
            startIcon={<StopIcon />}
            onClick={() => handleBotControl('stop')}
            disabled={controlMutation.isLoading}
          >
            Διακοπή
          </Button>
          
          <Button
            variant="contained"
            color="warning"
            startIcon={<RestartAltIcon />}
            onClick={() => handleBotControl('restart')}
            disabled={controlMutation.isLoading}
          >
            Επανεκκίνηση
          </Button>

          <Button
            variant="outlined"
            color="info"
            startIcon={<ClearAllIcon />}
            onClick={handleClearCache}
            disabled={clearCacheMutation.isLoading}
          >
            Καθαρισμός Cache
          </Button>
        </Box>

        {(controlMutation.isLoading || clearCacheMutation.isLoading) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">
              {controlMutation.isLoading ? 'Εκτέλεση εντολής...' : 'Καθαρισμός cache...'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Bot System Settings */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          🔧 Bot System Configuration
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.manual_enabled}
                  onChange={(e) => handleSettingsChange('manual_enabled', e.target.checked)}
                />
              }
              label="Χειροκίνητη Ενεργοποίηση Bot System"
            />
            <Typography variant="body2" color="text.secondary">
              Ενεργοποιεί/απενεργοποιεί τελείως το Bot System
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.schedule_enabled}
                  onChange={(e) => handleSettingsChange('schedule_enabled', e.target.checked)}
                  disabled={!settings.manual_enabled}
                />
              }
              label="Προγραμματισμένη Λειτουργία"
            />
            <Typography variant="body2" color="text.secondary">
              Λειτουργεί μόνο σε συγκεκριμένες ώρες
            </Typography>
          </Grid>

          {(settings.schedule_enabled && settings.manual_enabled) ? (
            <>
              <Grid item xs={6}>
                <TextField
                  label="Ώρα Έναρξης"
                  type="time"
                  value={settings.start_time}
                  onChange={(e) => handleSettingsChange('start_time', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  label="Ώρα Λήξης"
                  type="time"
                  value={settings.end_time}
                  onChange={(e) => handleSettingsChange('end_time', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Το Bot System θα λειτουργεί καθημερινά από {settings.start_time} μέχρι {settings.end_time} ({settings.timezone})
                </Typography>
              </Grid>
            </>
          ) : null}

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* 🐛 NEW: Debug Settings Section */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.debug_enabled}
                  onChange={(e) => handleSettingsChange('debug_enabled', e.target.checked)}
                />
              }
              label="Debug Console Messages"
            />
            <Typography variant="body2" color="text.secondary">
              Εμφανίζει/κρύβει debug console.log messages στο Live Monitoring και άλλες σελίδες
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              💡 Tip: Ενεργοποιήστε το για troubleshooting, απενεργοποιήστε το για καθαρότερα browser logs
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSaveSettings}
                disabled={updateSettingsMutation.isLoading}
              >
                {updateSettingsMutation.isLoading ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Αποθήκευση...
                  </>
                ) : (
                  'Αποθήκευση Ρυθμίσεων'
                )}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Settings;
